import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';

import { makeGetMasterToken } from 'selectors/auth';
import { makeGetExternalSSOAccounts } from 'selectors/external-accounts';

import { removeExternalAccount } from 'actions/external-accounts';

import { SSO_ACCOUNTS_OPTIONS, getOAuthStateParam } from 'services/external-accounts-service';
import { useFeatureAvailable } from 'shared-hooks/feature';

import Tooltip from 'components/tooltip';
import PlanUpgradeButton from 'components/PlanUpgradeButton';

const ExternalAccount = ({ id, icon, name, connectUrl, checkFeatureAvailable = true, account, t, onInstall, onRemove }) => {
    const msSSOAvailable = useFeatureAvailable('microsoftSSO')

    const connectDisabled = useMemo(() => {
        if (!checkFeatureAvailable) {
            return false;
        }

        if (id !== 'microsoft') {
            return false;
        }

        return !msSSOAvailable;
    }, [id, checkFeatureAvailable, msSSOAvailable])

    const handleRemoveClick = () => {
        onRemove(account.id);
    }

    const handleInstallClick = () => {
        onInstall && onInstall(id);
    }

    return (
        <Tooltip
            className="light help-tooltip"
            disabled={!connectDisabled}
            tooltip={
                <Trans
                    i18nKey="billing:upgrade.microsoftSSO"
                    components={{
                        title: <strong />,
                        button: <PlanUpgradeButton type="microsoftSSO" />,
                        LearnMore: <a href="http://help.niftypm.com/en/articles/3059016-time-tracking-reporting" target="_blank" rel="noopener noreferrer" />
                    }}
                    values={{ plan: t('billing:plans.Business.title') }}
                />
            }
            placement="bottom-start"
        >
            <div className="external-account-card">
                <div className="card-head">
                    <div className="card-icon">
                        { icon }
                    </div>

                    <div className="card-title">
                        { name }

                        {
                            account ? <small>{ account.email }</small> : null
                        }
                    </div>
                </div>

                <div className="card-actions">
                    {
                        account ? (
                            <button className="btn btn-link" onClick={handleRemoveClick}>{ t('common:Disconnect') }</button>
                        ) : (
                            <a href={connectUrl} target="_blank" rel="noreferrer" disabled={connectDisabled} className="btn btn-link" onClick={handleInstallClick}>{ t('common:Connect') }</a>
                        )
                    }
                </div>
            </div>
        </Tooltip>
    );
}

const UserSSOSettings = ({ withTitle = true, showGoogle = true, showMicrosoft = true, checkFeatureAvailable, masterToken, externalAccounts, t }) => {
    const remove = useCallback(id => {
        return removeExternalAccount(id, true);
    }, []);

    const handleAccountRemove = useCallback(id => {
        remove(id);
    }, [remove]);

    const getExistingAccount = useCallback((provider) => {
        return externalAccounts.find(account => account.provider === provider);
    }, [externalAccounts]);

    const urlState = useMemo(() => {
        return getOAuthStateParam(masterToken, false);
    }, [masterToken]);

    const options = useMemo(() => {
        return SSO_ACCOUNTS_OPTIONS
            .filter(option => {
                if (option.provider === 'google' && !showGoogle) {
                    return false;
                }

                if (option.provider === 'microsoft' && !showMicrosoft) {
                    return false;
                }

                return true;
            })
    }, [showGoogle, showMicrosoft]);

    return (
        <div className="form">
            <div className="form-row">
                <div className="field-holder">
                    {
                        withTitle ? (
                            <div className="field-label">
                                { t('settings:sso.title') }
                            </div>
                        ) : null
                    }

                    <div className="external-accounts-list type-list">
                        {
                            options.map(option => (
                                <ExternalAccount
                                    key={option.provider}
                                    id={option.provider}
                                    icon={option.icon}
                                    name={option.name}
                                    checkFeatureAvailable={checkFeatureAvailable}
                                    connectUrl={option.noState ? option.connectUrl : `${option.connectUrl}&state=${urlState} sso~true`}
                                    account={getExistingAccount(option.provider)}
                                    t={t}
                                    onRemove={handleAccountRemove}
                                />
                            ))
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getExternalSSOAccounts = makeGetExternalSSOAccounts();
    const getMasterToken = makeGetMasterToken();

    return ({ externalAccounts, auth }, ownProps) => {
        return {
            externalAccounts: getExternalSSOAccounts(externalAccounts.items, false),
            masterToken: getMasterToken(auth)
        }
    };
}

export default connect(makeMapStateToProps)(withTranslation(['apps', 'settings', 'common'])(UserSSOSettings));
